/**
 * Attribute Permissions Adapter
 * 
 * Connects attribute governance to the three-layer authorization model.
 * Runtime authorization uses capability IDs only (no legacy role names).
 * 
 * Attribute permissions flow:
 * 1. CAPABILITY: User has required capability (e.g. VALUE_ENTER, VALUE_ACCEPT)
 * 2. SCOPE: Permission applies at the attribute's org unit scope
 * 3. POLICY: Range status and other context (e.g. range accepted before propose)
 */

import type { PermissionAction, PermissionScope } from '../../types/prioritization/permissions';
import type { CapabilityId } from '../../types/prioritization/capabilityIds';
import {
  getCapabilitiesForAction,
  CANONICAL_CAPABILITY_IDS,
} from '../../types/prioritization/capabilityIds';
import { checkAuthorization, type UnifiedAuthResult } from './unifiedAuthorization';
import type { PolicyRule, FullEvalContext } from './types';

// ============================================================================
// Types
// ============================================================================

/**
 * Attribute governance configuration (from attributeGovernanceStore).
 * Legacy role fields (rangeDefinerRoles, etc.) are kept for template storage only;
 * runtime checks use required capabilities only.
 */
export interface AttributeGovernanceConfig {
  attributeKey: string;
  cycleId?: string;
  
  // Legacy: stored in templates; not used for runtime authorization
  rangeDefinerRoles: string[];
  rangeApproverRoles: string[];
  valueProposerRoles: string[];
  valueApproverRoles: string[];
  
  // Range status
  rangeStatus?: 'pending' | 'proposed' | 'accepted';
}

/**
 * Attribute permission action types
 */
export type AttributeAction = 
  | 'values.define_range'
  | 'values.approve_range'
  | 'values.propose'
  | 'values.approve';

/**
 * Result of attribute permission check
 */
export interface AttributePermissionResult extends UnifiedAuthResult {
  /** Whether the range is accepted (for value actions) */
  rangeAccepted?: boolean;
  /** The governance config that was used */
  governanceConfig?: AttributeGovernanceConfig;
}

// ============================================================================
// Policy Rules for Attribute Governance
// ============================================================================

/** Capability required to define value range for an attribute. */
const RANGE_DEFINE_CAPABILITY: CapabilityId = 'VALUE_RANGE_DEFINE';
/** Capability required to approve value range. */
const RANGE_APPROVE_CAPABILITY: CapabilityId = 'VALUE_RANGE_APPROVE';
/** Capability required to propose values. */
const VALUE_PROPOSE_CAPABILITY: CapabilityId = 'VALUE_ENTER';
/** Capability required to approve/accept values. */
const VALUE_APPROVE_CAPABILITY: CapabilityId = 'VALUE_ACCEPT';

/**
 * Create policy rules for a specific attribute governance config.
 * Rules use actor capability IDs only (no legacy role names).
 */
export function createAttributeGovernanceRules(
  config: AttributeGovernanceConfig
): PolicyRule[] {
  const { attributeKey } = config;
  
  return [
    // Rule: Allow define_range if user has VALUE_RANGE_DEFINE (or SCALES_DEFINE for backward compat)
    {
      id: `attr-range-define-${attributeKey}`,
      effect: 'allow',
      actions: ['values.define_range'],
      when: (ctx: FullEvalContext) => {
        const caps = (ctx.actor as { capabilityIds?: CapabilityId[] }).capabilityIds ?? [];
        return caps.includes(RANGE_DEFINE_CAPABILITY) || caps.includes('SCALES_DEFINE');
      },
      reasons: () => [{ code: 'CAPABILITY_ALLOWED', data: { capability: RANGE_DEFINE_CAPABILITY } }],
      description: `Range definer capability for ${attributeKey}`,
    },
    
    // Rule: Allow approve_range if user has VALUE_RANGE_APPROVE
    {
      id: `attr-range-approve-${attributeKey}`,
      effect: 'allow',
      actions: ['values.approve_range'],
      when: (ctx: FullEvalContext) => {
        const caps = (ctx.actor as { capabilityIds?: CapabilityId[] }).capabilityIds ?? [];
        return caps.includes(RANGE_APPROVE_CAPABILITY);
      },
      reasons: () => [{ code: 'CAPABILITY_ALLOWED', data: { capability: RANGE_APPROVE_CAPABILITY } }],
      description: `Range approver capability for ${attributeKey}`,
    },
    
    // Rule: Allow propose if user has VALUE_ENTER
    {
      id: `attr-value-propose-${attributeKey}`,
      effect: 'allow',
      actions: ['values.propose'],
      when: (ctx: FullEvalContext) => {
        const caps = (ctx.actor as { capabilityIds?: CapabilityId[] }).capabilityIds ?? [];
        return caps.includes(VALUE_PROPOSE_CAPABILITY);
      },
      reasons: () => [{ code: 'CAPABILITY_ALLOWED', data: { capability: VALUE_PROPOSE_CAPABILITY } }],
      description: `Value proposer capability for ${attributeKey}`,
    },
    
    // Rule: Allow approve if user has VALUE_ACCEPT
    {
      id: `attr-value-approve-${attributeKey}`,
      effect: 'allow',
      actions: ['values.approve'],
      when: (ctx: FullEvalContext) => {
        const caps = (ctx.actor as { capabilityIds?: CapabilityId[] }).capabilityIds ?? [];
        return caps.includes(VALUE_APPROVE_CAPABILITY);
      },
      reasons: () => [{ code: 'CAPABILITY_ALLOWED', data: { capability: VALUE_APPROVE_CAPABILITY } }],
      description: `Value approver capability for ${attributeKey}`,
    },
    
    // Rule: Deny value proposal if range not accepted
    {
      id: `attr-range-required-${attributeKey}`,
      effect: 'deny',
      actions: ['values.propose'],
      when: (ctx: FullEvalContext) => {
        const rangeStatus = ctx.context.extra?.rangeStatus as string | undefined;
        return rangeStatus !== 'accepted';
      },
      reasons: () => [{ 
        code: 'MISSING_PREREQUISITE', 
        data: { prerequisite: 'Range must be accepted before proposing values' } 
      }],
      description: `Range must be accepted for ${attributeKey}`,
    },
    
    {
      id: `attr-default-deny-${attributeKey}`,
      effect: 'deny',
      actions: ['values.*'],
      when: () => true,
      reasons: () => [{ code: 'NOT_PARTICIPANT' }],
      description: `Default deny for ${attributeKey}`,
    },
  ];
}

// ============================================================================
// Main API
// ============================================================================

/**
 * Check if user can perform an attribute governance action.
 * Uses capability IDs only (no legacy role names). Pass resolved userCapabilities.
 */
export function checkAttributePermission(
  userId: string,
  userCapabilities: CapabilityId[],
  action: AttributeAction,
  config: AttributeGovernanceConfig,
  scope?: PermissionScope
): AttributePermissionResult {
  const baseAction = getBasePermissionAction(action);
  const requiredCaps = getCapabilitiesForAction(baseAction);
  const hasRequiredCap = requiredCaps.some((c) => userCapabilities.includes(c));
  if (!hasRequiredCap) {
    return {
      allowed: false,
      blockedBy: 'capability',
      reasonCode: 'MISSING_CAPABILITY',
      reason: `Required capability for ${baseAction}: one of ${requiredCaps.join(', ')}`,
      rangeAccepted: config.rangeStatus === 'accepted',
      governanceConfig: config,
      debug: {
        capabilityPassed: false,
        scopePassed: false,
        policyPassed: false,
      },
    };
  }
  const policyRules = createAttributeGovernanceRules(config);
  const result = checkAuthorization({
    userId,
    userRoles: [],
    isAdmin:
      userCapabilities.includes('ADMIN_MANAGE_PERMISSIONS') ||
      (CANONICAL_CAPABILITY_IDS as readonly string[]).every((c) =>
        userCapabilities.includes(c as CapabilityId)
      ),
    actorCapabilityIds: userCapabilities,
    action: action as PermissionAction,
    targetScope: scope,
    resource: {
      type: 'attribute',
      id: config.attributeKey,
      attributes: { cycleId: config.cycleId },
    },
    context: {
      cycleId: config.cycleId,
      extra: { rangeStatus: config.rangeStatus },
    },
    policyRules,
  });
  return {
    ...result,
    rangeAccepted: config.rangeStatus === 'accepted',
    governanceConfig: config,
  };
}

/**
 * Quick check: Can user define value range? (capability-only; no legacy role names)
 */
export function canDefineRange(
  userCapabilities: CapabilityId[],
  _config: AttributeGovernanceConfig
): boolean {
  return (
    userCapabilities.includes('VALUE_RANGE_DEFINE') ||
    userCapabilities.includes('SCALES_DEFINE')
  );
}

/**
 * Quick check: Can user approve value range? (capability-only)
 */
export function canApproveRange(
  userCapabilities: CapabilityId[],
  _config: AttributeGovernanceConfig
): boolean {
  return userCapabilities.includes('VALUE_RANGE_APPROVE');
}

/**
 * Quick check: Can user propose values for this attribute? (capability-only)
 * Requires range to be accepted when requireRangeAccepted is true.
 */
export function canProposeValue(
  userCapabilities: CapabilityId[],
  config: AttributeGovernanceConfig,
  requireRangeAccepted: boolean = true
): boolean {
  if (requireRangeAccepted && config.rangeStatus !== 'accepted') {
    return false;
  }
  return userCapabilities.includes('VALUE_ENTER');
}

/**
 * Quick check: Can user approve values for this attribute? (capability-only)
 */
export function canApproveValue(
  userCapabilities: CapabilityId[],
  _config: AttributeGovernanceConfig
): boolean {
  return userCapabilities.includes('VALUE_ACCEPT');
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Map attribute-specific action to base permission action (for capability check).
 * Uses PermissionActions that map to capability IDs: VALUE_RANGE_DEFINE, VALUE_RANGE_APPROVE, VALUE_ENTER, VALUE_ACCEPT.
 */
function getBasePermissionAction(action: AttributeAction): PermissionAction {
  switch (action) {
    case 'values.define_range':
      return 'attributes.define';
    case 'values.approve_range':
      return 'attributes.assign_owner';
    case 'values.propose':
      return 'values.propose';
    case 'values.approve':
      return 'values.accept';
    default:
      return 'values.propose';
  }
}
